import React, { useState } from 'react';
import {
  useFloating,
  useInteractions,
  useTransitionStyles,
  useDismiss,
  flip,
  shift,
  useClick,
  useHover,
  safePolygon,
} from '@floating-ui/react';

import Popover from './Popover';

const PopoverContainer = ({
  isHover = false,
  placement,
  mode,
  yOffset,
  xOffset,
  ...restProps
}: {
  isHover?: any;
  placement?: any;
  mode?: any;
  yOffset?: any;
  xOffset?: any;
} & Record<string, any>) => {
  const [open, setOpen] = useState(
    mode === 'controlled' ? restProps.open : false,
  );

  const { x, y, strategy, refs, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: placement || 'right-end',
    middleware: [flip(), shift()],
  });
  const { isMounted, styles } = useTransitionStyles(context, {
    initial: {
      opacity: 0,
      transform: 'translateY(-10px)',
    },
    open: { opacity: 1, transform: 'translateY(0)' },
  });

  const click = useClick(context, { enabled: !isHover });
  const hover = useHover(context, {
    enabled: isHover,
    handleClose: safePolygon(),
    restMs: 40,
    delay: { open: 0, close: 150 },
  });
  const dismiss = useDismiss(context, {
    outsidePress: true,
    ancestorScroll: !isHover,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions(
    isHover ? [hover, dismiss] : [click, dismiss],
  );

  const styleProps = {
    position: strategy,
    top: y + (yOffset || 0) || 0,
    left: x + (xOffset || 0) || 0,
  };

  return (
    <Popover
      {...restProps}
      isHover={isHover}
      isMounted={isMounted}
      refs={refs}
      animationStyles={styles}
      styleProps={styleProps}
      placement={placement}
      mode={mode}
      getReferenceProps={getReferenceProps}
      getFloatingProps={getFloatingProps}
    />
  );
};

PopoverContainer.defaultProps = {
  mode: 'uncontrolled',
  yOffset: 0,
  xOffset: 0,
};

export default React.memo(PopoverContainer) as any;
